import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { vehicleApi } from '../services/api';
import { VIBER_INVITE_URL } from './ViberJoinButton';

interface Rates {
  usd: number;
  cad: number;
}

const ViberIcon = (
  <svg viewBox="0 0 48 48" width="15" height="15" style={{ flex: 'none' }} aria-hidden="true">
    <circle cx="24" cy="24" r="24" fill="#9333ea" />
    <path
      fill="#fff"
      d="M35.2 12.8C33.4 11 28.8 8.6 24.2 8.4h-.1c-4.6.2-9.2 2.6-11 4.4-1.4 1.4-2.8 3.8-3.2 7.2-.4 3.4-.1 7.4 2.2 10.4v4.4c0 .6.4 1.2 1 1.4.2.1.4.1.6.1.4 0 .8-.2 1-.4l2.8-2.8c1.2.3 2.4.4 3.6.4h.1c4.6-.2 9.2-2.6 11-4.4 1.4-1.4 2.8-3.8 3.2-7.2.4-3.4.1-7.4-2.2-10.1z"
    />
  </svg>
);

const StatsTicker: React.FC = () => {
  // Shares the react-query cache with HomePage's 'statistics' query (no extra request)
  const { data: statistics } = useQuery('statistics', vehicleApi.getStatistics);
  const [rates, setRates] = useState<Rates>({ usd: 0.95, cad: 0.68 });

  useEffect(() => {
    let active = true;
    const fetchRates = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const res = await fetch('https://api.exchangerate-api.com/v4/latest/EUR', {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const data = await res.json();
        if (active && data?.rates) {
          setRates({ usd: 1 / data.rates.USD, cad: 1 / data.rates.CAD });
        }
      } catch {
        /* keep fallback rates */
      }
    };
    fetchRates();
    const interval = setInterval(fetchRates, 30 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const stats: any = statistics;
  const nf = (n: number) => n.toLocaleString('bg-BG');

  const Group: React.FC<{ dup?: boolean }> = ({ dup }) => (
    <div className="cc-ticker__group" aria-hidden={dup || undefined}>
      <span className="cc-ticker__item">
        <span className="cc-ticker__dot" style={{ background: '#3b82f6' }} />
        <span><b>{nf(stats?.total_vehicles || 0)}</b> Автомобили</span>
      </span>
      <span className="cc-ticker__item">
        <span className="cc-ticker__dot" style={{ background: '#22c55e' }} />
        <span><b>{nf(stats?.active_auctions || 0)}</b> Активни търгове</span>
      </span>
      <span className="cc-ticker__item">
        <span className="cc-ticker__dot" style={{ background: '#f59e0b' }} />
        <span>🇺🇸 USD <b>{rates.usd.toFixed(2)} €</b></span>
      </span>
      <span className="cc-ticker__item">
        <span className="cc-ticker__dot" style={{ background: '#f59e0b' }} />
        <span>🇨🇦 CAD <b>{rates.cad.toFixed(2)} €</b></span>
      </span>
      <a
        className="cc-ticker__item cc-ticker__cta"
        href={VIBER_INVITE_URL}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={dup ? -1 : undefined}
      >
        <span className="cc-ticker__dot" style={{ background: '#9333ea' }} />
        {ViberIcon}
        <span><b>Влезте във Viber групата за ежедневни ТОП оферти!</b></span>
      </a>
    </div>
  );

  return (
    <div className="cc-ticker" role="region" aria-label="Статистика и валутни курсове">
      <div className="cc-ticker__track">
        <Group />
        <Group dup />
        <Group dup />
      </div>
      <style>{`
        .cc-ticker{position:relative;overflow:hidden;
          -webkit-mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent);
                  mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent)}
        .cc-ticker__track{display:flex;width:max-content;animation:cc-ticker-scroll 30s linear infinite}
        .cc-ticker:hover .cc-ticker__track{animation-play-state:paused}
        .cc-ticker__group{display:flex;flex:0 0 auto;align-items:center;padding:11px 0}
        .cc-ticker__item{display:inline-flex;align-items:center;gap:8px;white-space:nowrap;padding:0 22px;font-size:14px;color:#374151;border-right:1px solid #eef0f3;text-decoration:none}
        .cc-ticker__item b{font-weight:800;color:#111827}
        .cc-ticker__dot{width:8px;height:8px;border-radius:50%;flex:none;box-shadow:0 0 0 3px rgba(0,0,0,.05)}
        .cc-ticker__cta{color:#7e22ce}
        .cc-ticker__cta b{color:#7e22ce}
        .cc-ticker__cta:hover{text-decoration:underline}
        .dark .cc-ticker__item{color:#d1d5db;border-right-color:#374151}
        .dark .cc-ticker__item b{color:#f3f4f6}
        .dark .cc-ticker__cta,.dark .cc-ticker__cta b{color:#c084fc}
        .dark .cc-ticker__dot{box-shadow:0 0 0 3px rgba(255,255,255,.06)}
        @keyframes cc-ticker-scroll{from{transform:translateX(0)}to{transform:translateX(-33.3333%)}}
        @media (prefers-reduced-motion:reduce){.cc-ticker__track{animation:none}}
      `}</style>
    </div>
  );
};

export default StatsTicker;
